import { Monitor, Moon, Sun } from 'lucide-react';
import { useTheme, type ThemeChoice } from '../../hooks/useTheme';
import { cn } from '../../lib/cn';

const NEXT_LABEL: Record<ThemeChoice, string> = {
  light: 'Switch to dark theme',
  dark: 'Switch to system theme',
  system: 'Switch to light theme',
};

const CURRENT_LABEL: Record<ThemeChoice, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

/**
 * Cycles light → dark → system.
 *
 * The accessible name states the *next* state ("Switch to dark theme") rather
 * than the current one, because that is the action the user is taking. The
 * current state is conveyed by the icon and by the visible label on wide
 * screens, and read out via `title` on hover.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, cycleTheme } = useTheme();

  return (
    <button
      type="button"
      onClick={cycleTheme}
      title={`Theme: ${CURRENT_LABEL[theme]}`}
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-field border border-line bg-card px-2.5',
        'text-sm text-muted transition-colors hover:bg-sunken hover:text-strong',
        className
      )}
    >
      <span aria-hidden="true" className="grid place-items-center [&>svg]:size-4">
        {theme === 'light' ? <Sun /> : theme === 'dark' ? <Moon /> : <Monitor />}
      </span>
      <span className="sr-only-live">{NEXT_LABEL[theme]}</span>
      <span aria-hidden="true" className="hidden sm:inline">
        {CURRENT_LABEL[theme]}
      </span>
    </button>
  );
}
